import {
  changeSort,
  changeTen99Filters,
  clear1099s,
  clearSort,
  EmptyTen99Filter,
  FilterEnum,
  getTen99s,
  Ten99StatusFilter
} from 'Agent/1099s/ducks/1099';
import {Ten99} from 'Agent/1099s/models/1099';
import {FeatureConstants, getOrCreateFeatureFlag} from 'Core/ducks/featureFlag';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {toUndefined} from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import {withValueChanged} from 'Shared/utilities/withValueChanged';

export const get1099FileName = (ten99: Ten99) => `1099_${ten99.accountNumber}_${ten99.taxYear}`;

export const getTen99DownloadUrl = (ten99FeatureFlag: boolean, documentId: string, encryptedIdentifier: string) => {
  const documentUri = ten99FeatureFlag ? 'document' : `${documentId}/document`;
  return `/api/commissions/v1/taxStatements/${documentUri}${addFilterParameters({encryptedIdentifier})}`;
};

type Ten99sProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

const Ten99s = (props: Ten99sProps) => {
  const dispatch = useDispatch();
  useBreadcrumbs('1099s', props.match.url);
  const {selectedAgency} = props;
  const {ten99sFilter, sort, ten99s} = useSelector((state: ReducerState) => state.ten99);
  const {Tax1099Form} = useSelector((state: ReducerState) => state.featureFlag);
  const agentNumbers = useSelector((state: ReducerState) => state.user.profile.currentAgentNumbers);

  const requestMethod = Tax1099Form ? RequestMethod.POST : RequestMethod.GET;
  const fileExtension = Tax1099Form ? FileExtension.PDF : FileExtension.TIFF;

  const taxForms: FilterEnum[] = [
    {
      name: '1099-MISC',
      value: '1',
    },
    {
      name: '1099-NEC',
      value: '2',
    },
    {
      name: '1099-INT',
      value: '3',
    },
    {
      name: '1099-R',
      value: '4',
    },
  ];

  useEffect(() => {
    dispatch(getOrCreateFeatureFlag(FeatureConstants.TEN_99_PRO));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clear1099s());
      dispatch(changeTen99Filters(EmptyTen99Filter));
      dispatch(clearSort());
    };
  }, [clear1099s, changeTen99Filters]);

  useEffect(() => {
    dispatch(
      getTen99s(
        agentNumbers,
        selectedAgency.encryptedIdentifier,
        selectedAgency.isNewYork,
        undefined,
        ten99sFilter,
        toUndefined(sort)
      )
    );
  }, [ten99sFilter, selectedAgency, getTen99s, sort]);

  const changeFilter = withValueChanged(ten99sFilter, filter => dispatch(changeTen99Filters(filter)));

  const filterProperties = {
    changeFilter,
    filterData: ten99sFilter,
  };

  return (
    <PagedView
      pagedViewKey="1099"
      loadMore={page => {
        dispatch(
          getTen99s(
            agentNumbers,
            selectedAgency.encryptedIdentifier,
            selectedAgency.isNewYork,
            page,
            ten99sFilter,
            toUndefined(sort)
          )
        );
      }}
      data={ten99s}
      sort={toUndefined(sort)}
      onChangeSort={updatedSort => dispatch(changeSort(updatedSort))}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="accountNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
            ddprivacy="mask"
          >
            {ten99 => ten99.accountNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="accountName"
            filterProperties={withStringFilterProperties(filterProperties, 'accountName')}
            default
            ddprivacy="mask"
          >
            {ten99 => ten99.accountName}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="history"
            filterProperties={withSelectFilterProperties(filterProperties, 'history', Ten99StatusFilter)}
            default
          >
            {ten99 => ten99.history}
          </Column>
          <Column
            {...columnProps}
            title="Tax Year"
            sortColumnKey="taxYear"
            filterProperties={withStringFilterProperties(filterProperties, 'taxYear')}
            default
          >
            {ten99 => ten99.taxYear}
          </Column>
          <Column
            {...columnProps}
            title="Tax Form"
            sortColumnKey="taxFormType"
            filterProperties={withSelectFilterProperties(filterProperties, 'taxFormType', taxForms)}
            default
          >
            {ten99 => (Tax1099Form ? ten99.tax1099FormType?.name : ten99.taxFormType?.name)}
          </Column>

          <Column {...columnProps} title="" isActions default>
            {ten99 =>
              ten99.documentId ? (
                <FileDownloadButton
                  extension={fileExtension}
                  downloadedFileName={get1099FileName(ten99)}
                  displayTooltip={false}
                  relativeFileLocation={getTen99DownloadUrl(
                    Tax1099Form,
                    ten99.documentId,
                    selectedAgency.encryptedIdentifier
                  )}
                  requestMethod={requestMethod}
                  requestPayload={{
                    documentId: ten99.documentId,
                    isTaxportSystem: ten99.isTaxportSystem,
                    ...(ten99.partyTaxFormFChar != null && { partyTaxFormFChar: ten99.partyTaxFormFChar }),
                    ...(ten99.partyTaxFormSeqNbr != null && { partyTaxFormSeqNbr: ten99.partyTaxFormSeqNbr })
                  }}
                 
                >
                  Download
                </FileDownloadButton>
              ) : (
                <div>N/A</div>
              )
            }
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default Ten99s;